import { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import 'swiper/css';
import 'swiper/css/pagination';
import i18n from '../i18n';
import OnboardingQuiz from './OnboardingQuiz';
import { useQuiz } from '../contexts/QuizContext';
import useUserStore from '../stores/userStore';

const Container = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background: #23272f;
  color: #fff;
  padding-top: var(--tg-content-safe-area-inset-top, 0px);
  padding-bottom: var(--tg-content-safe-area-inset-bottom, 0px);
`;

const Logo = styled.div`
  font-size: 32px;
  font-weight: bold;
  padding: 20px;
  text-align: center;
  color: #4b69ff;
`;

const SwiperContainer = styled.div`
  flex: 1;
  width: 100%;
  padding: 20px;
`;

const Slide = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 20px;
  height: 100%;
`;

const SlideImage = styled.img`
  width: 100%;
  max-width: 300px;
  height: auto;
  margin-bottom: 24px;
  border-radius: 16px;
`;

const SlideTitle = styled.h2`
  font-size: 24px;
  font-weight: bold;
  margin-bottom: 16px;
  text-align: center;
`;

const SlideDescription = styled.p`
  font-size: 16px;
  line-height: 1.5;
  color: #ccc;
  text-align: center;
  margin-bottom: 24px;
`;

const BottomSection = styled.div`
  padding: 20px;
  background: rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(10px);
  position: relative;
  z-index: 1000;
`;

const LanguageRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 16px;
  position: relative;
  z-index: 1000;
`;

const LanguageLabel = styled.label`
  font-size: 14px;
  color: #ccc;
`;


const CheckboxContainer = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 20px;
`;

const Checkbox = styled.input`
  width: 20px;
  height: 20px;
  margin-top: 2px;
`;

const CheckboxLabel = styled.label`
  font-size: 14px;
  color: #ccc;
  line-height: 1.4;
`;

const StartButton = styled.button`
  width: 100%;
  padding: 16px;
  border-radius: 12px;
  border: none;
  background: ${props => props.disabled ? 'rgba(75, 105, 255, 0.3)' : '#4b69ff'};
  color: #fff;
  font-size: 16px;
  font-weight: bold;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.2s;

  &:hover:not(:disabled) {
    background: #3a56d4;
  }
`;

const OnboardingView = ({ setAgreed }) => {
	const [isAgreed, setIsAgreed] = useState(false);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(null);
	const [checking, setChecking] = useState(true);
	const [selectedLanguage, setSelectedLanguage] = useState(() => localStorage.getItem('app_language') || i18n.language || 'en');
	const navigate = useNavigate();
	const { t } = useTranslation();
	const { showQuiz, setShowQuiz, setQuizCompleted } = useQuiz();
	const user = useUserStore(state => state.user);

	// Убираем проверку соглашений - сразу показываем квиз
	useEffect(() => {
		console.log('OnboardingView: useEffect запущен');
		setChecking(false);

		// Если пользователь еще не загружен, ждем
		if (!user) {
			console.log('OnboardingView: пользователь еще не загружен, ждем...');
			return;
		}

		// Проверяем, проходил ли пользователь квиз ранее
		const hasCompletedQuiz = localStorage.getItem('quizCompleted');
		console.log('OnboardingView: hasCompletedQuiz =', hasCompletedQuiz);

		// Проверяем, является ли пользователь новым (в течение 2 минут после регистрации)
		const timeDiff = user && user.createdAt ? (Date.now() - new Date(user.createdAt).getTime()) : null;
		const isNewUser = user && user.createdAt &&
			(timeDiff > 0 ? timeDiff < 2 * 60 * 1000 : true); // Если дата в будущем, считаем новым пользователем
		console.log('OnboardingView: isNewUser =', isNewUser, 'user.createdAt =', user?.createdAt, 'timeDiff =', timeDiff);
		console.log('OnboardingView: showQuiz до установки =', showQuiz);

		if (!hasCompletedQuiz && isNewUser) {
			console.log('OnboardingView: показываем квиз новому пользователю, вызываем setShowQuiz(true)');
			setShowQuiz(true);
			console.log('OnboardingView: setShowQuiz(true) вызван');
		} else {
			console.log('OnboardingView: квиз уже пройден или пользователь не новый, переходим на главную');
			// Если квиз уже пройден или пользователь не новый, сразу переходим на главную
			if (setAgreed) setAgreed(true);
			navigate('/');
		}
	}, [setAgreed, navigate, user]);

	const slides = [
		{
			image: '/assets/images/onboarding/slide1.png',
			title: t('onboarding.slide1.title'),
			description: t('onboarding.slide1.description')
		},
		{
			image: '/assets/images/onboarding/slide2.png',
			title: t('onboarding.slide2.title'),
			description: t('onboarding.slide2.description')
		},
		{
			image: '/assets/images/onboarding/slide3.png',
			title: t('onboarding.slide3.title'),
			description: t('onboarding.slide3.description')
		}
	];

	// Синхронизируем выбранный язык селекта с актуальным языком i18n
	useEffect(() => {
		const normalized = String(i18n.language || 'en').toLowerCase().split('-')[0];
		if (selectedLanguage !== normalized) {
			setSelectedLanguage(normalized);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [i18n.language]);

	const handleStart = () => {
		// Убираем проверку соглашений - сразу показываем квиз
		localStorage.setItem('termsAccepted', 'true');
		setShowQuiz(true);
	};

	// Логика показа квиза перенесена в основной useEffect выше

	const handleQuizComplete = () => {
		setShowQuiz(false);
		setQuizCompleted(true);
		// Сохраняем флаг о прохождении квиза
		localStorage.setItem('quizCompleted', 'true');
		// Только после завершения квиза устанавливаем agreed и переходим на главную
		if (setAgreed) setAgreed(true);
		navigate('/');
	};

	const supportedLangs = Object.keys(i18n.options?.resources || {});
	const languageNames = {
		en: 'English',
		ru: 'Русский',
		uz: "Oʻzbekcha",
		uk: 'Українська',
		tr: 'Türkçe',
		id: 'Bahasa Indonesia',
		ar: 'العربية',
		fr: 'Français',
		fa: 'فارسی',
		vi: 'Tiếng Việt',
		az: 'Azərbaycan',
		ge: 'ქართული',
		kk: 'Қазақ тілі',
		ky: 'Кыргызча',
		tj: 'Тоҷикӣ',
		mn: 'Монгол',
		sk: 'Slovenčina',
		zh: '中文',
		pl: 'Polski',
		kr: '한국어',
		pt: 'Português'
	};

	const languageOptions = supportedLangs.map(code => ({ value: code, label: languageNames[code] || code }));

	const customSelectStyles = {
		control: (provided, state) => ({
			...provided,
			backgroundColor: 'rgba(0,0,0,0.2)',
			border: '1px solid rgba(255,255,255,0.15)',
			borderRadius: 10,
			padding: '2px 2px',
			boxShadow: 'none',
			minHeight: 44,
			'&:hover': { borderColor: 'rgba(255,255,255,0.25)' }
		}),
		menu: (provided) => ({
			...provided,
			backgroundColor: '#2b2f38',
			border: '1px solid rgba(255,255,255,0.15)',
			zIndex: 9999
		}),
		option: (provided, state) => ({
			...provided,
			backgroundColor: state.isSelected ? '#3a56d4' : state.isFocused ? 'rgba(255,255,255,0.08)' : 'transparent',
			color: '#fff',
			cursor: 'pointer'
		}),
		singleValue: (provided) => ({
			...provided,
			color: '#fff'
		}),
		input: (provided) => ({
			...provided,
			color: '#fff'
		}),
		placeholder: (provided) => ({
			...provided,
			color: '#ccc'
		}),
		indicatorSeparator: () => ({ display: 'none' }),
		dropdownIndicator: (provided) => ({
			...provided,
			color: '#ccc'
		})
	};

	const handleLanguageChange = (option) => {
		const lang = option?.value;
		if (!lang) return;
		setSelectedLanguage(lang);
		i18n.changeLanguage(lang);
		localStorage.setItem('app_language', lang);
	};

	if (checking) {
		return <Container><div style={{ textAlign: 'center', marginTop: 64 }}>{t('common.loading') || 'Загрузка...'}</div></Container>;
	}

	// Показываем квиз сразу (страница приветствия закомментирована)
	if (showQuiz) {
		console.log('Showing quiz immediately');
		return <OnboardingQuiz onComplete={handleQuizComplete} />;
	}

	// Закомментированная страница приветствия
	/*
	return (
		<Container>
			<Logo>SkinForge</Logo>

			<SwiperContainer>
				<Swiper
					modules={[Pagination, Autoplay]}
					pagination={{ clickable: true }}
					spaceBetween={20}
					slidesPerView={1}
					autoplay={{ delay: 4000, disableOnInteraction: false }}
				>
					{slides.map((slide, index) => (
						<SwiperSlide key={index}>
							<Slide>
								<SlideImage src={slide.image} alt={slide.title} />
								<SlideTitle>{slide.title}</SlideTitle>
								<SlideDescription>{slide.description}</SlideDescription>
							</Slide>
						</SwiperSlide>
					))}
				</Swiper>
			</SwiperContainer>

			<BottomSection>
				<LanguageRow>
					<LanguageLabel htmlFor="lang-select">{t('profile.language')}</LanguageLabel>
					<div style={{ flex: 1 }}>
						<Select
							inputId="lang-select"
							value={languageOptions.find(o => o.value === selectedLanguage) || null}
							onChange={handleLanguageChange}
							options={languageOptions}
							styles={customSelectStyles}
							isSearchable={false}
							menuPlacement="auto"
						/>
					</div>
				</LanguageRow>
				<CheckboxContainer>
					<Checkbox
						type="checkbox"
						id="terms"
						checked={isAgreed}
						onChange={(e) => setIsAgreed(e.target.checked)}
					/>
					<CheckboxLabel htmlFor="terms">
						{t('onboarding.terms.prefix')}{' '}
						<a href="https://www.notion.so/TERMS-OF-SERVICE-20403b226f588097b37fead691134a65" target="_blank" rel="noopener noreferrer" style={{ color: '#4b69ff' }}>
							{t('onboarding.terms.agreement')}
						</a>{' '}
						{t('onboarding.terms.and')}{' '}
						<a href="https://www.notion.so/PRIVACY-POLICY-20403b226f58805896ffe221c5a77ff6" target="_blank" rel="noopener noreferrer" style={{ color: '#4b69ff' }}>
							{t('onboarding.terms.privacy')}
						</a>
					</CheckboxLabel>
				</CheckboxContainer>

				{error && <div style={{ color: 'red', marginBottom: 12 }}>{error}</div>}
				<StartButton
					onClick={handleStart}
					disabled={!isAgreed || loading}
				>
					{loading ? t('common.loading') : t('onboarding.start')}
				</StartButton>
			</BottomSection>
		</Container>
	);
	*/

	// Показываем квиз если нужно
	console.log('OnboardingView: рендер, showQuiz =', showQuiz, 'checking =', checking);
	console.log('OnboardingView: localStorage quizCompleted =', localStorage.getItem('quizCompleted'));
	console.log('OnboardingView: user =', user);
	console.log('OnboardingView: user.createdAt =', user?.createdAt);

	// Если пользователь еще не загружен, показываем загрузку
	if (!user) {
		console.log('OnboardingView: пользователь не загружен, показываем загрузку');
		return <Container><div style={{ textAlign: 'center', marginTop: 64 }}>{t('common.loading') || 'Загрузка...'}</div></Container>;
	}

	if (showQuiz) {
		console.log('OnboardingView: показываем OnboardingQuiz');
		return <OnboardingQuiz onComplete={handleQuizComplete} />;
	}

	// Показываем загрузку пока проверяем соглашения
	console.log('OnboardingView: показываем загрузку');
	return <Container><div style={{ textAlign: 'center', marginTop: 64 }}>{t('common.loading') || 'Загрузка...'}</div></Container>;
};

export default OnboardingView; 